import {
  AlertCircle,
  CheckCircle,
  Edit,
  Eye,
  Image as ImageIcon,
  Plus,
  Save,
  Search,
  Trash2,
  X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import API_BASE_URL, { API_ENDPOINTS, buildApiUrl } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext';
import './Categorias.css';

const Categorias = () => {
  const { authenticatedFetch } = useAuth();
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Estados para el modal
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create' o 'edit'


  // Estados para el formulario
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
    businessGroup: 'BIKES',
    showInBooking: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // Estados para búsqueda y paginación
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Estados para confirmación de eliminación
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Cargar categorías al montar el componente
  useEffect(() => {
    fetchCategories();
  }, []);

  // Filtrar categorías cuando cambie el término de búsqueda
  useEffect(() => {
    const filtered = categories.filter(category =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredCategories(filtered);
    setCurrentPage(1); // Resetear a la primera página al buscar
  }, [searchTerm, categories]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authenticatedFetch(buildApiUrl(API_ENDPOINTS.VEHICLE_CATEGORIES));

      if (!response.ok) {
        throw new Error('Error al cargar las categorías');
      }

      const data = await response.json();
      setCategories(data);
      setFilteredCategories(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = () => {
    setModalMode('create');
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      imageUrl: '',
      businessGroup: 'BIKES',
      showInBooking: true,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEditCategory = (category) => {
    setModalMode('edit');
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      imageUrl: category.imageUrl || '',
      businessGroup: category.businessGroup || 'BIKES',
      showInBooking: category.showInBooking !== false,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleViewCategory = (category) => {
    setModalMode('view');
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      imageUrl: category.imageUrl || '',
      businessGroup: category.businessGroup || 'BIKES',
      showInBooking: category.showInBooking !== false,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDeleteCategory = (category) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      setSubmitting(true);
      const response = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.VEHICLE_CATEGORIES}/${categoryToDelete.id}`, {
        method: 'DELETE'
      }));

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al eliminar la categoría');
      }

      setSuccess('Categoría eliminada exitosamente');
      setShowDeleteModal(false);
      setCategoryToDelete(null);
      fetchCategories(); // Recargar la lista
    } catch (err) {
      setError(err.message);
      console.error('Error deleting category:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'El nombre es requerido';
    }
    
    if (formData.name.trim().length < 2) {
      errors.name = 'El nombre debe tener al menos 2 caracteres';
    }
    
    if (formData.description && formData.description.length > 500) {
      errors.description = 'La descripción no puede exceder 500 caracteres';
    }
    
    if (formData.imageUrl && !isValidUrl(formData.imageUrl)) {
      errors.imageUrl = 'La URL de la imagen no es válida';
    }
    
    return errors;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitting(true);
      setFormErrors({});
      
      const url = modalMode === 'create' 
        ? buildApiUrl(API_ENDPOINTS.VEHICLE_CATEGORIES)
        : buildApiUrl(`${API_ENDPOINTS.VEHICLE_CATEGORIES}/${editingCategory.id}`);
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';
      
      const response = await authenticatedFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          imageUrl: formData.imageUrl.trim() || null,
          businessGroup: formData.businessGroup || 'BIKES',
          showInBooking: !!formData.showInBooking,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al guardar la categoría');
      }

      await response.json();
      
      setSuccess(
        modalMode === 'create' 
          ? 'Categoría creada exitosamente' 
          : 'Categoría actualizada exitosamente'
      );
      
      setShowModal(false);
      fetchCategories(); // Recargar la lista
    } catch (err) {
      setError(err.message);
      console.error('Error saving category:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      imageUrl: '',
      businessGroup: 'BIKES',
      showInBooking: true,
    });
    setFormErrors({});
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setCategoryToDelete(null);
  };

  // Calcular paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

  // Limpiar mensajes de éxito/error después de 5 segundos
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading) {
    return (
      <div className="categorias-page">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Cargando categorías...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="categorias-page">
        <div className="error-message">
          <h2>Error de Conexión</h2>
          <p>{error}</p>
          <button 
            className="btn-primary"
            onClick={() => {
              setError(null);
              fetchCategories();
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="categorias-page">
      <div className="page-header">
        <h1>Gestión de Categorías</h1>
        <button 
          className="btn-primary"
          onClick={handleCreateCategory}
        >
          <Plus size={16} />
          Nueva Categoría
        </button>
      </div>

      {/* Mensajes de éxito y error */}
      {success && (
        <div className="success-message">
          <CheckCircle size={20} />
          {success}
        </div>
      )}

      {/* Buscador y controles */}
      <div className="table-controls">
        <div className="search-container">
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar categorías..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="pagination-controls">
          <label>
            Mostrar:
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="items-per-page-select"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
            </select>
          </label>
        </div>
      </div>

      <div className="table-container">
        {filteredCategories.length === 0 ? (
          <div className="no-categories">
            {searchTerm ? (
              <p>No se encontraron categorías que coincidan con "{searchTerm}"</p>
            ) : (
              <p>No hay categorías registradas</p>
            )}
            {!searchTerm && (
              <button 
                className="btn-primary"
                onClick={handleCreateCategory}
              >
                Registrar la primera categoría
              </button>
            )}
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Imagen</th>
                  <th>Nombre</th>
                  <th>Descripción</th>
                  <th>Modelos</th>
                  <th>Fecha Creación</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((category) => (
                  <tr key={category.id}>
                    <td>
                      {category.imageUrl ? (
                        <img 
                          src={category.imageUrl} 
                          alt={category.name}
                          className="category-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="category-image-placeholder"
                        style={{ display: category.imageUrl ? 'none' : 'flex' }}
                      >
                        <ImageIcon size={20} />
                      </div>
                    </td>
                    <td>
                      <div className="category-name">
                        <strong>{category.name}</strong>
                      </div>
                    </td>
                    <td>
                      {category.description ? (
                        <span title={category.description}>
                          {category.description.length > 50 
                            ? `${category.description.substring(0, 50)}...` 
                            : category.description
                          }
                        </span>
                      ) : (
                        <span className="no-description">Sin descripción</span>
                      )}
                    </td>
                    <td>
                      <span className="models-badge">
                        {category.modelsCount || 0} modelo{category.modelsCount !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td>
                      {new Date(category.createdAt).toLocaleDateString('es-ES')}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon"
                          onClick={() => handleViewCategory(category)}
                          title="Ver detalles"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handleEditCategory(category)}
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => handleDeleteCategory(category)}
                          title="Eliminar"
                          disabled={category.modelsCount > 0}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="pagination">
                <div className="results-info">
                  Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, filteredCategories.length)} de {filteredCategories.length} categorías
                </div>
                <div className="page-numbers">
                  <button
                    className="btn btn-small"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      className={`btn btn-small ${currentPage === page ? 'active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    className="btn btn-small"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de formulario */}
      {showModal && (
        <div className="form-overlay" onClick={closeModal}>
          <div className="form-container" onClick={(e) => e.stopPropagation()}>
            <div className="form-header">
              <h2>
                {modalMode === 'create' ? 'Nueva Categoría' : 
                 modalMode === 'edit' ? 'Editar Categoría' : 'Ver Categoría'}
              </h2>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="category-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nombre *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={formErrors.name ? 'error' : ''}
                    placeholder="Nombre de la categoría"
                    disabled={modalMode === 'view'}
                    required
                  />
                  {formErrors.name && (
                    <small className="error-text">{formErrors.name}</small>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Descripción</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className={formErrors.description ? 'error' : ''}
                    placeholder="Descripción de la categoría (opcional)"
                    disabled={modalMode === 'view'}
                    rows="4"
                  />
                  {formErrors.description && (
                    <small className="error-text">{formErrors.description}</small>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Grupo de negocio</label>
                  <select
                    value={formData.businessGroup}
                    disabled={modalMode === 'view'}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        businessGroup: v,
                        showInBooking: v === 'CARRITOS' ? false : true,
                      }));
                    }}
                  >
                    <option value="BIKES">Bicicletas (BIKES)</option>
                    <option value="CARRITOS">Carritos (CARRITOS) — solo admin</option>
                    <option value="BUGGY">Buggy</option>
                    <option value="JET_SKI">Jet ski</option>
                    <option value="PARASAILING">Parasailing</option>
                  </select>
                  <small style={{ color: '#666', display: 'block', marginTop: 6 }}>
                    Las categorías CARRITOS suelen ocultarse del portal público (ver siguiente opción).
                  </small>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!formData.showInBooking}
                      disabled={modalMode === 'view'}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, showInBooking: e.target.checked }))
                      }
                    />
                    Mostrar en el portal de reservas público
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>URL de Imagen</label>
                  <input
                    type="url"
                    name="imageUrl"
                    value={formData.imageUrl}
                    onChange={handleInputChange}
                    className={formErrors.imageUrl ? 'error' : ''}
                    placeholder="https://ejemplo.com/imagen.jpg"
                    disabled={modalMode === 'view'}
                  />
                  {formErrors.imageUrl && (
                    <small className="error-text">{formErrors.imageUrl}</small>
                  )}
                  {formData.imageUrl && (
                    <div className="image-preview">
                      <img 
                        src={formData.imageUrl} 
                        alt="Vista previa"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {modalMode !== 'view' && (
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                    disabled={submitting}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div className="loading-spinner" style={{ width: '16px', height: '16px' }}></div>
                        Guardando...
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Save size={16} />
                        {modalMode === 'create' ? 'Crear' : 'Guardar'}
                      </div>
                    )}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="form-overlay" onClick={closeDeleteModal}>
          <div className="form-container" onClick={(e) => e.stopPropagation()}>
            <div className="form-header">
              <h2>Confirmar Eliminación</h2>
              <button className="btn-close" onClick={closeDeleteModal}>
                <X size={24} />
              </button>
            </div>

            <div className="delete-confirmation">
              <p>¿Estás seguro de que quieres eliminar la categoría <strong>"{categoryToDelete?.name}"</strong>?</p>
              {categoryToDelete?.modelsCount > 0 && (
                <div className="error-message">
                  <strong>⚠️ No se puede eliminar:</strong> Esta categoría tiene {categoryToDelete.modelsCount} modelo(s) asociado(s).
                </div>
              )}
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeDeleteModal}
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={submitting || categoryToDelete?.modelsCount > 0}
              >
                {submitting ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div className="loading-spinner" style={{ width: '16px', height: '16px' }}></div>
                    Eliminando...
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Trash2 size={16} />
                    Eliminar
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categorias; 